import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiLoader, FiSend, FiX } from 'react-icons/fi';
import './FeedComposer.css';
import LocationAutocompleteInput from '../../../components/LocationAutocompleteInput';
import { REQUEST_SERVICE_TYPES, createRequest } from '../../../services/requestsApi';
import { createFeedPost } from '../../../services/feedPostsApi';
import { buildUserAvatarUrl } from '../../../services/profileApi';

const REQUEST_CURRENCY_OPTIONS = ['MXN', 'USD'];
const NEWS_COMPANY_CATEGORY_KEYS = new Set(['news', 'news_agent', 'news_agency']);
const normalizeCategoryKey = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const initialRequestForm = {
  title: '',
  serviceType: 'transport',
  pickupLocation: '',
  dropoffLocation: '',
  neededBy: '',
  budget: '',
  currency: REQUEST_CURRENCY_OPTIONS[0],
  details: '',
};

const buildInitials = (text = '') => {
  return text
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('') || 'F';
};

export default function FeedComposer({
  user,
  company,
  onRequestCreated,
  onPostCreated,
}) {
  const [activeTab, setActiveTab] = useState('post'); // 'post' | 'request'
  const [expanded, setExpanded] = useState(false);

  // Post state
  const [postContent, setPostContent] = useState('');
  const [savingPost, setSavingPost] = useState(false);
  const [postFeedback, setPostFeedback] = useState('');
  const [postFeedbackTone, setPostFeedbackTone] = useState('success');

  // Request state
  const [requestForm, setRequestForm] = useState({ ...initialRequestForm });
  const [savingRequest, setSavingRequest] = useState(false);
  const [requestFeedback, setRequestFeedback] = useState('');
  const [requestFeedbackTone, setRequestFeedbackTone] = useState('success');

  // News state
  const [newsContent, setNewsContent] = useState('');
  const [newsLink, setNewsLink] = useState('');
  const [savingNews, setSavingNews] = useState(false);
  const [newsFeedback, setNewsFeedback] = useState('');
  const [newsFeedbackTone, setNewsFeedbackTone] = useState('success');

  const companyId = company?.id || company?.companyId || '';
  const companyName =
    company?.name ||
    company?.empresaName ||
    company?.companyName ||
    '';
  const userId = user?.id || '';

  const hasCompany = Boolean(companyId);
  const canPublishAsCompany = hasCompany;
  const [postIdentity, setPostIdentity] = useState(canPublishAsCompany ? 'company' : 'user');
  const canPost = Boolean(userId);
  const canCreateRequests = Boolean(userId && hasCompany);

  const companyCategories = React.useMemo(() => {
    const list = [];
    const pushValue = value => {
      if (typeof value === 'string' && value.trim()) {
        list.push(value.trim());
      }
    };
    if (Array.isArray(company?.categories)) {
      company.categories.forEach(pushValue);
    }
    if (Array.isArray(company?.companyCategories)) {
      company.companyCategories.forEach(pushValue);
    }
    pushValue(company?.category);
    pushValue(company?.companyCategory);
    pushValue(company?.companyType);
    pushValue(company?.type);
    return [...new Set(list)];
  }, [company?.categories, company?.companyCategories, company?.category, company?.companyCategory, company?.companyType, company?.type]);

  const normalizedCompanyCategories = useMemo(
    () => companyCategories.map(normalizeCategoryKey),
    [companyCategories]
  );

  const isNewsCompany = normalizedCompanyCategories.some(value =>
    NEWS_COMPANY_CATEGORY_KEYS.has(value)
  );
  const canCreateNewsPosts = Boolean(userId && hasCompany && isNewsCompany);

  const userAvatarUrl = useMemo(
    () => buildUserAvatarUrl(user, '120x120') || user?.avatarUrl || '',
    [user]
  );

  const companyAvatarUrl = useMemo(() => {
    if (!hasCompany) return '';
    return company?.logoUrl || company?.logo || '';
  }, [company, hasCompany]);

  const userLabel = useMemo(() => {
    if (user?.name) return user.name;
    if (user?.email) return user.email;
    return 'Tu perfil';
  }, [user?.name, user?.email]);

  const userInitials = useMemo(() => {
    if (user?.name) return buildInitials(user.name);
    if (user?.email) return buildInitials(user.email);
    return 'U';
  }, [user?.name, user?.email]);

  useEffect(() => {
    if (!hasCompany) {
      setPostIdentity('user');
    }
  }, [hasCompany]);

  useEffect(() => {
    if (!canCreateRequests && activeTab === 'request') {
      setActiveTab('post');
    }
  }, [canCreateRequests, activeTab]);

  useEffect(() => {
    if (!canCreateNewsPosts && activeTab === 'news') {
      setActiveTab('post');
    }
  }, [canCreateNewsPosts, activeTab]);

  const isNewsMode = canCreateNewsPosts && activeTab === 'news';
  const effectivePostIdentity = isNewsMode ? 'company' : postIdentity;

  const activePublisherName =
    effectivePostIdentity === 'company' && hasCompany
      ? companyName || 'Tu empresa'
      : userLabel;

  const activeAvatarInitials =
    effectivePostIdentity === 'company' && hasCompany
      ? buildInitials(companyName || userLabel)
      : userInitials;
  const activeAvatarUrl =
    effectivePostIdentity === 'company' && companyAvatarUrl ? companyAvatarUrl : userAvatarUrl;

  const introMessage = isNewsMode
    ? 'Publicaras una noticia destacada para la red Flotte.'
    : effectivePostIdentity === 'company' && hasCompany
      ? 'Estas compartiendo como tu empresa. Puedes cambiarlo en Publicar como.'
      : 'Comparte novedades o publica un requerimiento sin salir del feed.';

  const handleCollapsedIdentityToggle = useCallback(() => {
    if (!canPublishAsCompany || isNewsMode) return;
    setPostIdentity(prev => {
      if (prev === 'company' && canPost) {
        return 'user';
      }
      if (prev === 'user' && hasCompany) {
        return 'company';
      }
      return prev;
    });
  }, [canPublishAsCompany, isNewsMode, canPost, hasCompany]);

  const expandedContentRef = useRef(null);
  const [expandedHeight, setExpandedHeight] = useState(0);

  useEffect(() => {
    if (!expanded) {
      setExpandedHeight(0);
      return;
    }
    const node = expandedContentRef.current;
    if (!node) return;

    const measure = () => {
      setExpandedHeight(node.scrollHeight);
    };
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure());
      observer.observe(node);
      return () => observer.disconnect();
    }

    const handleResize = () => measure();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [expanded, activeTab, postContent, newsContent, requestForm]);

  const handleOpenRequestTab = () => {
    if (!canCreateRequests) {
      setExpanded(true);
      setActiveTab('post');
      setPostFeedbackTone('error');
      setPostFeedback('Necesitas asociar tu empresa para crear solicitudes.');
      return;
    }
    setExpanded(true);
    setActiveTab('request');
    setPostFeedback('');
    setRequestFeedback('');
    setNewsFeedback('');
  };

  const handleOpenNewsTab = () => {
    if (!canCreateNewsPosts) {
      setExpanded(true);
      setActiveTab('post');
      setPostFeedbackTone('error');
      setPostFeedback('Solo las empresas de noticias pueden publicar este tipo de contenido.');
      return;
    }
    setExpanded(true);
    setActiveTab('news');
    setPostIdentity('company');
    setPostFeedback('');
    setRequestFeedback('');
    setNewsFeedback('');
  };

  const handleTabChange = tab => {
    if (tab === 'request') {
      handleOpenRequestTab();
      return;
    }
    if (tab === 'news') {
      handleOpenNewsTab();
      return;
    }
    setExpanded(true);
    setActiveTab(tab);
    setPostFeedback('');
    setRequestFeedback('');
    setNewsFeedback('');
  };

  const handleCollapse = (reset = false) => {
    setExpanded(false);
    setActiveTab('post');
    setPostFeedback('');
    setRequestFeedback('');
    setNewsFeedback('');
    if (reset) {
      setPostContent('');
      setRequestForm({ ...initialRequestForm });
      setNewsContent('');
      setNewsLink('');
      setNewsFeedbackTone('success');
    }
    setSavingNews(false);
  };

  const handlePostSubmit = async event => {
    event.preventDefault();
    if (!canPost) {
      setPostFeedbackTone('error');
      setPostFeedback('Necesitas iniciar sesion para publicar en el feed.');
      return;
    }
    if (!postContent.trim()) {
      setPostFeedbackTone('error');
      setPostFeedback('Escribe algun mensaje para publicarlo en tu red.');
      return;
    }

    setSavingPost(true);
    setPostFeedback('');

    try {
      const payload = {
        content: postContent,
        creatorId: userId,
        status: 'published',
        visibility: 'network',
      };

      if (postIdentity === 'company' && hasCompany) {
        payload.companyId = companyId;
      }

      const created = await createFeedPost(payload);
      if (typeof onPostCreated === 'function') {
        onPostCreated(created);
      }
      handleCollapse(true);
    } catch (error) {
      setPostFeedbackTone('error');
      setPostFeedback(error?.message || 'No pudimos publicar tu actualizacion.');
    } finally {
      setSavingPost(false);
    }
  };

  const handleNewsSubmit = async event => {
    event.preventDefault();
    if (!canCreateNewsPosts) {
      setNewsFeedbackTone('error');
      setNewsFeedback('Tu cuenta no tiene permisos para publicar noticias.');
      return;
    }
    if (!newsContent.trim()) {
      setNewsFeedbackTone('error');
      setNewsFeedback('Escribe el contenido de la noticia antes de publicarla.');
      return;
    }

    const trimmedLink = newsLink.trim();
    if (trimmedLink) {
      try {
        // eslint-disable-next-line no-new
        new URL(trimmedLink.startsWith('http') ? trimmedLink : `https://${trimmedLink}`);
      } catch {
        setNewsFeedbackTone('error');
        setNewsFeedback('Revisa el enlace de la noticia, parece no ser valido.');
        return;
      }
    }

    setSavingNews(true);
    setNewsFeedback('');
    setNewsFeedbackTone('success');

    try {
      const payload = {
        content: newsContent,
        creatorId: userId,
        status: 'published',
        visibility: 'network',
        companyId,
        isNews: true,
        newsLink: trimmedLink ? (trimmedLink.startsWith('http') ? trimmedLink : `https://${trimmedLink}`) : '',
      };
      const created = await createFeedPost(payload);
      if (typeof onPostCreated === 'function') {
        onPostCreated(created);
      }
      handleCollapse(true);
    } catch (error) {
      setNewsFeedbackTone('error');
      setNewsFeedback(error?.message || 'No pudimos publicar la noticia. Intenta nuevamente.');
    } finally {
      setSavingNews(false);
    }
  };

  const handleRequestFormChange = event => {
    const { name, value } = event.target;
    setRequestForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRequestSubmit = async event => {
    event.preventDefault();

    if (!canCreateRequests) {
      setRequestFeedbackTone('error');
      setRequestFeedback('Necesitas asociar tu empresa para crear solicitudes.');
      return;
    }

    if (!requestForm.title.trim()) {
      setRequestFeedbackTone('error');
      setRequestFeedback('Agrega un titulo para tu solicitud.');
      return;
    }

    const payload = {
      title: requestForm.title,
      serviceType: requestForm.serviceType,
      pickupLocation: requestForm.pickupLocation,
      dropoffLocation: requestForm.dropoffLocation,
      neededBy: requestForm.neededBy,
      budget: requestForm.budget,
      currency: requestForm.currency || REQUEST_CURRENCY_OPTIONS[0],
      details: requestForm.details,
      status: 'published',
      companyId,
      creatorId: userId,
    };

    setSavingRequest(true);
    setRequestFeedback('');
    let newRequest;
    try {
      newRequest = await createRequest(payload);
      if (typeof onRequestCreated === 'function') {
        onRequestCreated(newRequest);
      }

      let sharedSuccessfully = false;
      try {
        const defaultMessage = newRequest.title
          ? `Nueva solicitud: ${newRequest.title}`
          : 'Nueva solicitud publicada.';
        const message = requestForm.details?.trim()
          ? `${defaultMessage}\n\n${requestForm.details.trim()}`
          : defaultMessage;
        const post = await createFeedPost({
          content: message,
          companyId,
          creatorId: userId,
          status: 'published',
          visibility: 'network',
          attachedRequestId: newRequest.id,
        });
        if (typeof onPostCreated === 'function') {
          onPostCreated(post);
        }
        sharedSuccessfully = true;
      } catch (postError) {
        console.warn('Solicitud creada, pero fallo al publicarla en el feed.', postError);
        setRequestFeedbackTone('error');
        setRequestFeedback(
          'La solicitud se creo, pero no pudimos compartirla en el feed. Intenta nuevamente desde la pestana Publicacion.'
        );
      }
      if (sharedSuccessfully) {
        handleCollapse(true);
      }
    } catch (error) {
      setRequestFeedbackTone('error');
      setRequestFeedback(
        error?.message || 'No pudimos guardar tu solicitud. Intenta nuevamente.'
      );
    } finally {
      setSavingRequest(false);
    }
  };

  const renderPostTab = () => (
    <form className="feed-composer__form" onSubmit={handlePostSubmit}>
      <label className="feed-composer__field feed-composer__field--full">
        <span>Comparte una actualizacion</span>
        <textarea
          name="postContent"
          value={postContent}
          onChange={event => setPostContent(event.target.value)}
          placeholder="Comparte necesidades, logros o novedades de tu empresa..."
          rows={3}
          maxLength={1200}
        />
      </label>

      {postFeedback ? (
        <div className={`feed-composer__feedback feed-composer__feedback--${postFeedbackTone}`}>
          {postFeedback}
        </div>
      ) : null}

      <footer className="feed-composer__footer">
        <div className="feed-composer__footer-actions">
          <button
            type="button"
            className="feed-composer__secondary"
            onClick={() => handleCollapse(true)}
          >
            Cancelar
          </button>
          <button type="submit" className="feed-composer__primary" disabled={savingPost}>
            {savingPost ? (
              <>
                <FiLoader className="feed-composer__spinner" />
                Publicando...
              </>
            ) : (
              <>
                <FiSend />
                Publicar
              </>
            )}
          </button>
        </div>
      </footer>
    </form>
  );

  const renderNewsTab = () => (
    <form className="feed-composer__form" onSubmit={handleNewsSubmit}>
      <label className="feed-composer__field feed-composer__field--full">
        <span>Escribe tu noticia</span>
        <textarea
          name="newsContent"
          value={newsContent}
          onChange={event => setNewsContent(event.target.value)}
          placeholder="Comparte comunicados, novedades del sector o actualizaciones importantes..."
          rows={4}
          maxLength={1500}
        />
      </label>
      <label className="feed-composer__field feed-composer__field--full">
        <span>Enlace de referencia (opcional)</span>
        <input
          type="text"
          name="newsLink"
          value={newsLink}
          onChange={event => setNewsLink(event.target.value)}
          placeholder="https://www.ejemplo.com/noticia"
          inputMode="url"
        />
      </label>
      <small className="feed-composer__hint">
        Las noticias se muestran debajo del mapa de actividad para todos los usuarios.
      </small>
      {newsFeedback ? (
        <div className={`feed-composer__feedback feed-composer__feedback--${newsFeedbackTone}`}>
          {newsFeedback}
        </div>
      ) : null}
      <footer className="feed-composer__footer">
        <div className="feed-composer__footer-actions">
          <button
            type="button"
            className="feed-composer__secondary"
            onClick={() => handleCollapse(false)}
          >
            Cancelar
          </button>
          <button type="submit" className="feed-composer__primary" disabled={savingNews}>
            {savingNews ? (
              <>
                <FiLoader className="feed-composer__spinner" />
                Publicando...
              </>
            ) : (
              <>
                <FiSend />
                Publicar noticia
              </>
            )}
          </button>
        </div>
      </footer>
    </form>
  );

  const renderRequestTab = () => (
    <form className="feed-composer__form" onSubmit={handleRequestSubmit}>
      <div className="feed-composer__grid">
        <label className="feed-composer__field feed-composer__field--full">
          <span>Titulo</span>
          <input
            type="text"
            name="title"
            value={requestForm.title}
            onChange={handleRequestFormChange}
            placeholder="Transporte urgente CDMX - Monterrey"
            maxLength={160}
            required
          />
        </label>

        <label className="feed-composer__field">
          <span>Tipo de servicio</span>
          <select
            name="serviceType"
            value={requestForm.serviceType}
            onChange={handleRequestFormChange}
            required
          >
            {REQUEST_SERVICE_TYPES.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="feed-composer__field">
          <span>Fecha requerida</span>
          <input
            type="date"
            name="neededBy"
            value={requestForm.neededBy}
            onChange={handleRequestFormChange}
          />
        </label>

        <label className="feed-composer__field">
          <span>Origen</span>
          <LocationAutocompleteInput
            name="pickupLocation"
            value={requestForm.pickupLocation}
            onChange={handleRequestFormChange}
            placeholder="Ciudad de México, CDMX"
          />
        </label>

        <label className="feed-composer__field">
          <span>Destino</span>
          <LocationAutocompleteInput
            name="dropoffLocation"
            value={requestForm.dropoffLocation}
            onChange={handleRequestFormChange}
            placeholder="Monterrey, NL"
          />
        </label>

        <label className="feed-composer__field">
          <span>Presupuesto</span>
          <input
            type="text"
            name="budget"
            value={requestForm.budget}
            onChange={handleRequestFormChange}
            placeholder="Ej. $45,000 MXN"
            maxLength={120}
          />
        </label>
        <label className="feed-composer__field">
          <span>Divisa</span>
          <select
            name="currency"
            value={requestForm.currency}
            onChange={handleRequestFormChange}
          >
            {REQUEST_CURRENCY_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="feed-composer__field feed-composer__field--full">
        <span>Detalles adicionales</span>
        <textarea
          name="details"
          value={requestForm.details}
          onChange={handleRequestFormChange}
          rows={3}
          maxLength={1200}
          placeholder="Descripcion breve del recorrido, tipo de carga o instrucciones especiales."
        />
      </label>

      {requestFeedback ? (
        <div
          className={`feed-composer__feedback feed-composer__feedback--${requestFeedbackTone}`}
        >
          {requestFeedback}
        </div>
      ) : null}

      <footer className="feed-composer__footer">
        <button
          type="button"
          className="feed-composer__link"
          onClick={() => setActiveTab('post')}
        >
          Volver a publicacion
        </button>
        <div className="feed-composer__footer-actions">
          <button
            type="button"
            className="feed-composer__secondary"
            onClick={() => handleCollapse(true)}
          >
            Cancelar
          </button>
          <button type="submit" className="feed-composer__primary" disabled={savingRequest}>
            {savingRequest ? (
              <>
                <FiLoader className="feed-composer__spinner" />
                Guardando...
              </>
            ) : (
              'Crear solicitud'
            )}
          </button>
        </div>
      </footer>
    </form>
  );

  if (!expanded) {
    return (
      <section className="feed-composer feed-card feed-composer--collapsed">
        <div className="feed-composer__collapsed-body">
          <div
            className={`feed-composer__avatar${
              activeAvatarUrl ? ' feed-composer__avatar--image' : ''
            }`}
            aria-hidden="true"
          >
            {activeAvatarUrl ? <img src={activeAvatarUrl} alt="" /> : activeAvatarInitials}
          </div>
          <button
            type="button"
            className="feed-composer__collapsed-button"
            onClick={() => handleTabChange('post')}
          >
            ¿Qué te gustaría compartír?
          </button>
        </div>
        <div className="feed-composer__quick-actions">
          <span className="feed-composer__collapsed-hint">
            Publicarás como{' '}
            {canPublishAsCompany ? (
              <button
                type="button"
                className="feed-composer__identity-toggle"
                onClick={handleCollapsedIdentityToggle}
                disabled={isNewsMode}
              >
                {effectivePostIdentity === 'company' ? (companyName || 'Tu empresa') : userLabel}
              </button>
            ) : (
              <strong>{activePublisherName}</strong>
            )}
          </span>
          {canCreateNewsPosts ? (
            <button
              type="button"
              className="feed-composer__quick-action"
              onClick={() => handleTabChange('news')}
            >
              <FiSend />
              Nueva noticia
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <div className="feed-composer-shell">
      <div
        className={`feed-composer-expander${expanded ? ' feed-composer-expander--open' : ''}`}
        style={{ maxHeight: expanded ? '9999px' : 0 }}
      >
        <section
          ref={expandedContentRef}
          className={`feed-composer feed-card feed-composer--expanded${expanded ? ' feed-composer--visible' : ''}`}
          style={{ minHeight: expanded ? (expandedHeight ? `${expandedHeight}px` : 'auto') : 0 }}
        >
          <header className="feed-composer__header">
            <div
              className={`feed-composer__avatar${
                activeAvatarUrl ? ' feed-composer__avatar--image' : ''
              }`}
              aria-hidden="true"
            >
              {activeAvatarUrl ? <img src={activeAvatarUrl} alt="" /> : activeAvatarInitials}
            </div>
            <div className="feed-composer__intro">
              <strong>{activePublisherName}</strong>
              <span>{introMessage}</span>
            </div>
            <button
              type="button"
              className="feed-composer__close"
              onClick={() => handleCollapse(true)}
              aria-label="Cerrar editor de publicaciones"
            >
              <FiX />
            </button>
          </header>

          <div className="feed-composer__tabs" role="tablist" aria-label="Modo de publicacion">
            <button
              type="button"
              role="tab"
              className={`feed-composer__tab${activeTab === 'post' ? ' active' : ''}`}
              aria-selected={activeTab === 'post'}
              onClick={() => handleTabChange('post')}
            >
              Publicacion
            </button>
            {canCreateNewsPosts ? (
              <button
                type="button"
                role="tab"
                className={`feed-composer__tab${activeTab === 'news' ? ' active' : ''}`}
                aria-selected={activeTab === 'news'}
                onClick={() => handleOpenNewsTab()}
              >
                Noticia
              </button>
            ) : null}
            <button
              type="button"
              role="tab"
              className={`feed-composer__tab${activeTab === 'request' ? ' active' : ''}`}
              aria-selected={activeTab === 'request'}
              onClick={handleOpenRequestTab}
            >
              Solicitud
            </button>
          </div>

          {activeTab === 'post'
            ? renderPostTab()
            : activeTab === 'news' && canCreateNewsPosts
              ? renderNewsTab()
              : renderRequestTab()}
        </section>
      </div>
    </div>
  );
}
